'use client'
import { cloneElement, forwardRef, ForwardRefRenderFunction, ReactElement, Ref, useCallback, useEffect, useMemo, useState } from 'react'
import { Flex, FlexC, FlexProps, FlexR } from './Flex'
import { IVirtualTableProvider } from './IVirtualTableProvider'
import { VirtualTableAdapter } from './VirtualTableAdapter'
import { DataRecord } from './ListViewAdapter'
import React from 'react'
import { Box, styled, alpha, lighten, useTheme } from '@mui/material'
import anylogger from '@app/anylogger'
import { convertLength, toUnitless } from './cssUtils'
import { MouseTouchEvent, useCurrentRef, useLocalStorageOrState } from '@app/hooks'
import { SortField } from '@app/utils'
import { TitleDefinition, TitleDefinitionList } from './TitleDefinition'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import { useVirtualRender, EntryRegistration } from './hooks/useVirtualRender'
import { DraggableFlex } from './DraggableFlex'

const log = anylogger('VirtualTable')

/**
 * Maybe use margin on the content instead of row gap so that the titles don't need fiddling (adding column gap)
 * * Might need a container for each element, but that would mess with the control of the scrolling for embedded controls
 * Test that rowgap and column gap work properly, or change to margin
 * ! Get Paged logs working in DBLogViewer
 * ! Allow Reordering/dragging of columns
 * ! Test Normal table with string values
 * ! Allow horizontal scroll
 * ! Allow auto-sizing of columns based on data, like VirtualListView
 */

class ITableContext {}
const TableContext = React.createContext<ITableContext>(undefined!)

interface VirtualTableProps<T extends Object = any> extends FlexProps {
	provider: IVirtualTableProvider<T>
	showTitles?: boolean
	rowGap?: string
	colGap?: string
	localStorageKey?: string
	/**
	 * The default sort field(s)
	 */
	defaultSort?: SortField | SortField[]
	/**
	 * This indicates the typical height of a row.  This height is used when rendering an invisible item that is not in the viewport.
	 * It defaults to 1.5em.
	 */
	defaultHeight?: string
	/**
	 * Allows filtering the items in the list
	 */
	filterText?: string

	/**
	 * the delay in milliseconds after the last change in filterText after which the filter is performed
	 * Default: 500ms
	 */
	filterDelay?: number
}
/**
 * This table component is build on a Flex component.  You can control the vertical spacing with the gap prop.
 */
export const VirtualTable = forwardRef(function InnerTable<T extends Object = any>(props: VirtualTableProps<T>, passedRef: any) {
	const {
		provider,
		showTitles = true,
		rowGap = '0px',
		colGap = '0.5em',
		localStorageKey,
		defaultHeight = '1.5em',
		defaultSort = [],
		filterText = '',
		filterDelay = 500,
		...rest
	} = props
	const [refreshCount, setRefreshCount] = useState(0)

	const [containerRef, setContainerRef] = useCurrentRef<HTMLElement>(passedRef)

	const createKey = useCallback((key: string, localStorageKey?: string): string | undefined => {
		return localStorageKey ? localStorageKey + '.' + key : undefined
	}, [])
	const [userTitles, setUserTitles] = useLocalStorageOrState<TitleDefinitionList>(createKey('userTitles', localStorageKey), [])
	const [sortFields, setSortFields] = useLocalStorageOrState<SortField[]>(
		createKey('sortFields', localStorageKey),
		Array.isArray(defaultSort) ? defaultSort : [defaultSort]
	)

	const refresh = useCallback(() => {
		setRefreshCount((prev) => prev + 1)
	}, [])

	const [adapter] = useState<VirtualTableAdapter>(new VirtualTableAdapter(refresh))
	useEffect(() => {
		if (!provider) return
		const titles = (userTitles.length ? userTitles : provider.getAllTitles ? provider.getAllTitles() : undefined) ?? []
		adapter.setProvider(provider, titles, () => {})
	}, [adapter, provider, userTitles])
	useEffect(() => {
		if (!adapter) return
		adapter.setFilterText(filterText, filterDelay)
	}, [adapter, filterText, filterDelay])

	const titleClick = useCallback(
		(titleDef: TitleDefinition, isCtrl: boolean) => {
			if (!adapter.titles?.length) return

			setSortFields((prev) => {
				let res = [...prev]

				let srt = res.find((fld) => fld.field == titleDef.name)
				if (srt) srt.isDesc = !srt.isDesc
				else {
					srt = { field: titleDef.name, isDesc: false, isNumeric: titleDef.isNumeric }
					// we can only use Ctrl ifit is a mouse event
					if (isCtrl) {
						res.push(srt)
					} else {
						res = [srt]
					}
				}

				return res
			})
		},
		[adapter.titles, setSortFields]
	)

	const getTitles = useCallback(() => {
		if (!showTitles || !adapter.titles) return null
		return (
			<TableTitles
				provider={provider}
				titles={adapter.titles}
				colGap={colGap}
				titleClick={titleClick}
				sortFields={sortFields}
				setUserTitles={setUserTitles}
			/>
		)
	}, [adapter.titles, colGap, provider, setUserTitles, showTitles, sortFields, titleClick])

	const createVirtualRenderWrapper = useVirtualRender(containerRef, defaultHeight, {
		rootMargin: '50% 0px 50% 0px',
		threshold: 0.1
	})

	const getRows = useCallback(() => {
		if (!adapter?.items) return null
		return adapter.items.map((item, index) => {
			return createVirtualRenderWrapper(
				index,
				<TableRow key={index} index={index} adapter={adapter} columnGap={colGap} />,
				adapter.getValue(index, 0)
			)
		})
	}, [adapter, colGap, createVirtualRenderWrapper])

	useEffect(() => {
		if (!adapter) return

		adapter.setSort(sortFields)
	}, [adapter, sortFields])

	return (
		<FlexC justifyContent="flex-start" rowGap={rowGap} {...rest}>
			{getTitles()}
			<FlexC ref={setContainerRef} fill justifyContent="flex-start" rowGap={rowGap} scroll>
				{getRows()}
			</FlexC>
		</FlexC>
	)
}) as <T extends Object = any>(p: VirtualTableProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement

interface TableTitlesProps {
	titles: TitleDefinitionList
	colGap: string
	sortFields: SortField[]
	provider: IVirtualTableProvider

	titleClick: (title: TitleDefinition, isCtrl: boolean) => void
	setUserTitles: (titles: TitleDefinitionList) => void
}
const TableTitles = React.forwardRef(function TableTitles(props: TableTitlesProps, ref: any) {
	const { titles, colGap, sortFields, titleClick, setUserTitles, provider } = props
	const [resizing, setResizing] = useState(false)

	const theme = useTheme()
	const convert = convertLength(theme.typography.htmlFontSize + 'px')
	const gapPx = toUnitless(convert(colGap, 'px'))

	// const onDragging = useCallback(
	// 	(isDragging: boolean) => {
	// 		setDragging(isDragging)
	// 		if (isDragging) closeContextMenu()
	// 	},
	// 	[closeContextMenu]
	// )
	const onResizing = useCallback((isResizing: boolean) => {
		setResizing(isResizing)
		// if (isResizing) closeContextMenu()
	}, [])
	// const titlesChanged = useCallback(
	// 	(oldIdx: number, newIdx: number) => {
	// 		// clone the titles so that we don't mutate the original reference
	// 		const res = [...titles]

	// 		res.splice(newIdx, 0, ...res.splice(oldIdx, 1))

	// 		doUserTitles(res)
	// 	},
	// 	[doUserTitles, titles]
	// )
	const onResized = useCallback(
		(idx: number, newSize: number | undefined) => {
			let newTitles = [...titles]
			newTitles[idx] = { ...newTitles[idx] }
			let td = newTitles[idx]
			if (typeof newSize == 'undefined') {
				if (provider.getAllTitles) td.width = provider.getAllTitles()[idx].width
			} else {
				if (newSize < 10) newSize = 10
				newSize = newSize - gapPx
				// @ts-ignore
				td.width = newSize
			}

			setUserTitles(newTitles)
		},
		[titles, setUserTitles, provider, gapPx]
	)

	const onClick = useCallback(
		(idx: number, e: MouseTouchEvent) => {
			titleClick(titles[idx], e.mouseEvent ? e.mouseEvent.ctrlKey : false)
		},
		[titles, titleClick]
	)

	const getTitles = useCallback(() => {
		return titles.map((title, index) => {
			let sizeProps: any = {}
			if (title.width) {
				let titleWidth = toUnitless(convert(title.width || '100px', ''))
				if (index > 0) titleWidth += gapPx
				sizeProps = { width: titleWidth }
			} else if (title.autoSize) {
				sizeProps = { flexGrow: 1 }
			}
			const sortField: SortField | undefined = sortFields.find((srt: SortField) => srt.field == title.name)

			return <TableTitle key={index} title={title} index={index} sortField={sortField} {...sizeProps} />
		})
	}, [titles, convert, gapPx, sortFields])

	return (
		<DraggableFlex
			gap="0px"
			axis="x"
			onItemClick={onClick}
			allowResizing={true}
			onResized={onResized}
			allowDragging={false}
			onResizing={onResizing}
		>
			{getTitles()}
		</DraggableFlex>
	)
})

interface TableTitleProps {
	title: TitleDefinition
	sortField?: SortField
}
const TableTitle = React.forwardRef(function TableTitle(props: TableTitleProps, ref: any) {
	const { title, sortField, ...rest } = props

	return (
		<VirtualTableTitle ref={ref} {...rest}>
			{title.name}
			<SortImage sorted={sortField} isAsc={!sortField || !sortField.isDesc} />
		</VirtualTableTitle>
	)
})
const SortImage = (props: any) => {
	const { sorted, isAsc, gap } = props

	if (!sorted) return null

	return isAsc ? (
		<ArrowDropUp
			sx={{
				fontSize: 'inherit'
			}}
		/>
	) : (
		<ArrowDropDown
			sx={{
				fontSize: 'inherit'
			}}
		/>
	)
}

interface TableRowProps {
	index: number
	adapter: VirtualTableAdapter
	columnGap?: string
}
const TableRow = forwardRef(function TableRow(props: TableRowProps, passedRef: any) {
	const { adapter, index, columnGap } = props

	const getColumns = useCallback(() => {
		return adapter.titles.map((title, idx) => {
			const sizeProps = title.width ? { width: title.width, flexShrink: 0 } : title.autoSize ? { flexGrow: 1 } : {}
			// return adapter.getDisplayValue(index, idx)
			return (
				<Flex
					key={idx}
					sx={{
						borderWidth: '0 1px 0 0',
						borderStyle: 'solid',
						borderColor: 'lightgray',
						overflow: 'hidden',
						...sizeProps
					}}
				>
					{adapter.getDisplayValue(index, idx)}
				</Flex>
			)
		})
	}, [adapter, index])

	return (
		<FlexR
			columnGap={columnGap}
			alignItems="stretch"
			sx={{
				borderWidth: '1px 1px 0 1px',
				borderStyle: 'solid',
				borderColor: 'lightgray',
				'&:last-child': { borderWidth: '1px' }
			}}
		>
			{getColumns()}
		</FlexR>
	)
})

export const VirtualTableTitle = styled(Box, {
	name: 'VirtualListView',
	slot: 'Title',
	overridesResolver: (props, styles) => {
		const { ownerState, theme, rightAligned, centered } = props
		// const { isDown } = ownerState
		return [
			styles.title
			// isDown && { [`&.${listViewClasses.isDown}`]: styles.titleIsDown },
			// rightAligned && { [`&.${listViewClasses.rightAligned}`]: styles.titleRightAligned },
			// centered && { [`&.${listViewClasses.centered}`]: styles.titleCentered }
		]
	}
})(({ theme }) => {
	return {
		backgroundColor: lighten(theme.palette.primary.light, 0.7),
		border: `outset 1px ${lighten(theme.palette.primary.light, 0.5)}`,
		fontWeight: 'bold',
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'clip',
		paddingLeft: '0.25em', // this is overridden if it rightAligned or centered
		// [`&.${listViewClasses.rightAligned}`]: {
		// 	paddingLeft: '0px',
		// 	paddingRight: '0.25em',
		// 	textAlign: 'right'
		// },
		// [`&.${listViewClasses.centered}`]: {
		// 	paddingLeft: '0.125em',
		// 	paddingRight: '0.125em',
		// 	textAlign: 'center'
		// },
		// [`&.${listViewClasses.isDown}`]: {
		// 	border: `inset 1px ${lighten(theme.palette.primary.light, 0.3)}`
		// },
		'&:hover': {
			fontWeight: '800',
			color: theme.palette.primary.main
		}
	}
})
